import React, { useEffect, useState } from 'react';

// SERVICES
import { getUserInfo } from 'js/library/utils/helpers';

// STYLES
import {
  Box,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  InputBase,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

// ASSETS
import AddIcon from '@mui/icons-material/Add';

// COMPONENTS
import ImportImage from 'js/components/TriiboComponents/ImportImage';
import Filter from './Filter';
import AutocompleteListType from './AutocompleteListType';

export default function CardList({
  isNewCard,
  formCard,
  setFormCard,
  tags,
  setTags,
  cardDefault,
  setCardDefault,
  setScrollPosition,
  cardDefaultDetails,
  setCardDefaultDetails,
  setIsActiveOverflow,
  modalRef,
  handlePublished,
  isPrivateCard,
  handlePrivacityCard,
  chooseDate,
  handleDateDefined,
  handleDates,
  selectedCardType,
  setSelectedCardType,
  cardTypes,
  handleAction,
  listType,
  setListType,
  setAppliedFilters,
  selectedEstablishment,
  setSelectedEstablishment,
  selectedStore,
  setSelectedStore,
  selectedProduct,
  setSelectedProduct,
  selectedIdEdit,
  infosForEdit,
  selectedKeywords,
  setSelectedKeywords,
}) {
  const [returnType, setReturnType] = useState(['physical']);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);

  const userInfo = getUserInfo();
  const isAdmin = userInfo.admin === true;

  // Atualiza o valor conforme as regras
  useEffect(() => {
    // Se a ordenação for por distância, força 'physical'
    if (formCard.orderType === 'location') {
      setReturnType(['physical']);
      updateCardReturnField(['physical']);
    }
    // Se não for 'selecionados_unitariamente', permite ambos
    else if (listType !== 'selecionados_unitariamente') {
      setReturnType(['physical', 'digital']);
      updateCardReturnField(['physical', 'digital']);
    }
  }, [formCard.orderType, listType, selectedCardType]);

  // Função para atualizar o campo correto no formCard
  const updateCardReturnField = (value) => {
    const updatedForm = { ...formCard };

    switch (selectedCardType.action) {
      case 'establishment_list':
        updatedForm.establishmentType = Array.isArray(value) ? value : [value];
        break;
      case 'promotion_list':
        updatedForm.promotionType = Array.isArray(value) ? value : [value];
        break;
      case 'product_list':
        updatedForm.productType = Array.isArray(value) ? value : [value];
        break;
      default:
        break;
    }
    setFormCard(updatedForm);
  };

  // Efeito para carregar os tipos corretos durante a edição
  useEffect(() => {
    if (!isNewCard && infosForEdit?.storePlacesQuery) {
      // Determina qual campo olhar baseado no tipo de card
      let types = [];
      switch (infosForEdit.action) {
        case 'establishment_list':
          types = infosForEdit.storePlacesQuery.establishmentType || [];
          break;
        case 'promotion_list':
          types = infosForEdit.storePlacesQuery.promotionType || [];
          break;
        case 'product_list':
          types = infosForEdit.storePlacesQuery.productType || [];
          break;
        default:
          types = ['physical']; // Fallback
      }

      // Garante que pelo menos um tipo está selecionado
      if (types.length === 0) {
        types = ['physical'];
      }

      setReturnType(types);
      updateCardReturnField(types);
    }
  }, [isNewCard, infosForEdit]);

  // Função para lidar com a mudança de seleção
  const handleReturnTypeChange = (type) => {
    let newReturnType;

    if (returnType.includes(type)) {
      // Se já está selecionado, remove
      newReturnType = returnType.filter((t) => t !== type);
    } else {
      // Se não está selecionado, adiciona
      newReturnType = [...returnType, type];
    }

    // Garante que pelo menos um tipo está selecionado
    if (newReturnType.length === 0) {
      newReturnType = [type === 'physical' ? 'digital' : 'physical'];
    }

    setReturnType(newReturnType);
    updateCardReturnField(newReturnType);
  };

  // Renderização condicional
  const shouldShowRadioGroup =
    listType !== 'selecionados_unitariamente' || formCard.orderType !== 'location';

  const handleAddTag = () => {
    const newTag = inputValue.trim();
    if (newTag && !tags.includes(newTag)) {
      setTags((prev) => [...prev, newTag]);
    }
    setInputValue('');
  };

  const handleDeleteTag = (tagToDelete) => {
    setTags((prev) => prev.filter((tag) => tag !== tagToDelete));
  };

  const handleKeyDown = (e) => {
    if ((e.key === 'Enter' || e.key === ',') && inputValue.trim()) {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleChangeListType = (event) => {
    setListType(event.target.value);
  };

  return (
    <Stack spacing={3}>
      {/* Seção de Título e Descrição */}
      <Stack spacing={2}>
        <TextField
          required
          id="outlined-basic"
          label="Título do card"
          inputProps={{ maxLength: 100 }}
          fullWidth
          value={formCard.title}
          onChange={(event) => setFormCard({ ...formCard, title: event.target.value })}
          onBlur={(e) => e.preventDefault()}
          onFocus={(e) => e.preventDefault()}
        />

        <TextField
          required
          id="outlined-multiline-flexible"
          label="Descrição"
          type="text"
          multiline
          fullWidth
          value={formCard.description}
          onChange={(e) => setFormCard({ ...formCard, description: e.target.value })}
          onBlur={(e) => e.preventDefault()}
          onFocus={(e) => e.preventDefault()}
        />
      </Stack>

      {/* Seção de Tipo de Card e Filtros */}
      <Stack spacing={2}>
        <FormControl fullWidth>
          <InputLabel id="card-type-label">Tipo de card</InputLabel>
          <Select
            required
            labelId="card-type-label"
            id="card-type"
            value={selectedCardType?.action || ''}
            label="Tipo de card"
            onChange={(e) => {
              const selected = cardTypes.find((card) => card.action === e.target.value);
              setSelectedCardType(selected);
              handleAction(e);
            }}
          >
            {cardTypes.map((card) => (
              <MenuItem key={card.action} value={card.action}>
                {card.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {selectedCardType.action && (
          <Filter
            cardType={selectedCardType.action}
            onFiltersChange={(filters) => setAppliedFilters(filters)}
            infosForEdit={infosForEdit}
          />
        )}
      </Stack>

      {/* Seção de Listagem e Tags */}
      <Stack spacing={2}>
        <FormControl fullWidth>
          <InputLabel id="tipo-listagem-label">Tipo de listagem</InputLabel>
          <Select
            labelId="tipo-listagem-label"
            id="tipo-listagem"
            value={listType}
            label="Tipo de listagem"
            onChange={handleChangeListType}
          >
            <MenuItem value="proximos_mim">Próximos a mim</MenuItem>
            <MenuItem value="selecionados_tag">Selecionados por tag</MenuItem>
            <MenuItem value="selecionados_unitariamente">Selecionados unitariamente</MenuItem>
            <MenuItem value="pointsback">PointsBack</MenuItem>
          </Select>
        </FormControl>

        {listType === 'selecionados_tag' && (
          <Box>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Tags
            </Typography>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 1,
                p: 1,
                border: '1px solid #ccc',
                borderRadius: 1,
                minHeight: '56px',
                '&:focus-within': {
                  borderColor: 'primary.main',
                },
              }}
            >
              {tags.map((tag, index) => (
                <Chip key={index} label={tag} onDelete={() => handleDeleteTag(tag)} size="small" />
              ))}

              <Box sx={{ flexGrow: 1, minWidth: '120px' }}>
                <InputBase
                  placeholder="Adicionar tags"
                  required={tags.length < 1}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  fullWidth
                  sx={{ px: 1 }}
                />
              </Box>

              <IconButton onClick={handleAddTag} size="small">
                <AddIcon />
              </IconButton>
            </Box>

            {listType === 'selecionados_tag' && tags.length === 0 && isNewCard && (
              <Typography variant="body2" color="error" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                *TAG OBRIGATÓRIA EX: triibo
              </Typography>
            )}
          </Box>
        )}

        {listType === 'selecionados_unitariamente' && formCard.action && (
          <AutocompleteListType
            typeCard={formCard.action}
            loading={loading}
            setLoading={setLoading}
            selectedEstablishment={selectedEstablishment}
            setSelectedEstablishment={setSelectedEstablishment}
            selectedStore={selectedStore}
            setSelectedStore={setSelectedStore}
            selectedProduct={selectedProduct}
            setSelectedProduct={setSelectedProduct}
            selectedIdEdit={selectedIdEdit}
          />
        )}

        {isAdmin && (
          <FormControl fullWidth>
            <InputLabel id="keywords-label">Keywords</InputLabel>
            <Select
              labelId="keywords-label"
              label="Keywords"
              id="keywords"
              multiple
              value={selectedKeywords}
              onChange={(e) => setSelectedKeywords(e.target.value)}
              renderValue={(selected) => selected.join(', ')}
            >
              {formCard?.keywords?.map((keyword) => (
                <MenuItem key={keyword} value={keyword}>
                  <Checkbox checked={selectedKeywords.includes(keyword)} />
                  <ListItemText primary={keyword} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Stack>

      {/* Seção de Ordenação e Tipo de Retorno */}
      <Stack spacing={2}>
        <FormControl required fullWidth>
          <InputLabel id="order-type-label">Ordenação</InputLabel>
          <Select
            required
            labelId="order-type-label"
            id="order-type"
            value={formCard.orderType === 'alphabetic' ? 'alphabetical' : formCard.orderType}
            label="Ordenação"
            onChange={(event) => setFormCard({ ...formCard, orderType: event.target.value })}
          >
            <MenuItem value="location">Distância</MenuItem>
            <MenuItem value="relevance">Prioridade</MenuItem>
            <MenuItem value="alphabetical">Alfabética A-Z</MenuItem>
          </Select>
        </FormControl>

        {shouldShowRadioGroup && selectedCardType.label && (
          <FormControl component="fieldset">
            <FormLabel component="legend">Tipo de retorno:</FormLabel>
            <Box display="flex" alignItems="center">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={returnType.includes('physical')}
                    onChange={() => handleReturnTypeChange('physical')}
                    name="physical"
                  />
                }
                label={`${selectedCardType.label} Físicos`}
              />

              {formCard.orderType !== 'location' && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={returnType.includes('digital')}
                      onChange={() => handleReturnTypeChange('digital')}
                      name="digital"
                    />
                  }
                  label={`${selectedCardType.label} Digitais`}
                />
              )}
            </Box>
          </FormControl>
        )}
      </Stack>

      {/* Seção de Imagens */}
      <Box>
        <Typography variant="subtitle1" fontWeight="bold" textAlign="center" mb={2}>
          Imagem do card:
        </Typography>
        <Typography textAlign="center" mb={3}>
          Selecione a imagem para seu card:
        </Typography>

        <Stack spacing={3}>
          <Box>
            <Typography variant="subtitle2" fontWeight="bold" mb={1}>
              Imagem para desktop e mobile:
            </Typography>
            <ImportImage
              imageDefault={true}
              autoWidth={true}
              image={cardDefault}
              setImage={setCardDefault}
              ratio={2.81 / 1}
              allowFreeCrop={true}
              refScroll={modalRef}
              setScrollPosition={setScrollPosition}
              setIsActiveOverflow={setIsActiveOverflow}
            />
          </Box>
          <Box>
            <Typography variant="subtitle2" fontWeight="bold" mb={1}>
              Imagem para detalhes do card:
            </Typography>
            <ImportImage
              imageDefault={true}
              autoWidth={true}
              image={cardDefaultDetails}
              setImage={setCardDefaultDetails}
              ratio={1.33 / 1}
              allowFreeCrop={true}
              refScroll={modalRef}
              setScrollPosition={setScrollPosition}
              setIsActiveOverflow={setIsActiveOverflow}
            />
          </Box>
        </Stack>
      </Box>

      {/* Seção de Configurações Adicionais */}
      <Stack spacing={2}>
        <FormControlLabel
          control={<Switch checked={chooseDate} onChange={handleDateDefined} color="primary" />}
          label="Definir data de ativação/expiração do card?"
        />

        {chooseDate && (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              fullWidth
              id="datetime-local"
              label="Data de ativação"
              type="datetime-local"
              value={formCard.enableDate}
              onChange={handleDates('enableDate')}
              InputLabelProps={{ shrink: true }}
              required
            />
            <TextField
              fullWidth
              id="datetime-local"
              label="Data de desativação"
              type="datetime-local"
              value={formCard.disableDate}
              onChange={handleDates('disableDate')}
              InputLabelProps={{ shrink: true }}
              required
            />
          </Stack>
        )}

        <FormControlLabel
          control={
            <Switch checked={isPrivateCard} onChange={handlePrivacityCard} color="primary" />
          }
          label="Visível apenas para membros?"
        />

        <FormControlLabel
          control={<Switch checked={formCard.isPublished} onChange={handlePublished} />}
          label="Card ativo?"
        />
      </Stack>
    </Stack>
  );
}
